package com.neurobs

import scala.collection.{TraversableLike, mutable}
import scala.collection.generic.CanBuildFrom
import scala.concurrent.{ExecutionContext, Future}
import scala.util.control.NonFatal
import scala.util.{Failure, Success, Try}

package object errors {

	/** Interface to objects containing information about an error */
	trait ErrorReport {
		def description: String

		override def toString: String = description
	}

	case class StringErrorReport(description: String) extends ErrorReport
	case class ThrowableErrorReport(e: Throwable, prefix: Option[String] = None) extends ErrorReport {
		override def description = {
			val eMessage = e.getLocalizedMessage
			prefix match {
				case None => eMessage
				case Some(p) => s"$p: $eMessage"
			}
		}
	}

	// Implicit conversions
	implicit def toErrorReport(s: String): StringErrorReport = StringErrorReport(s)
	implicit def toErrorReport(e: Throwable): ThrowableErrorReport = ThrowableErrorReport(e)

	/** Represents either an object, or a report about the error that prevented
	 * return of the object */
	type Result[T] = Either[ErrorReport, T]

	def get[T](res: Result[T]): T = res match {
		case Left(e) => throw new Exception(e.description)
		case Right(t) => t
	}

	def errorResult[T](message: String): Result[T] = Left(StringErrorReport(message))

	def errorMessage(res: Result[_]): Option[String] = res match {
		case Left(error) => Some(error.description)
		case _ => None
	}

	def sequence[T](seq: Seq[Result[T]]): Result[Seq[T]] = {
		try {
			Right(seq.map(get(_)))
		}
		catch {
			case e: Throwable => Left(e)
		}
	}

	/** Returns the result of a block, or an error report if the block
	 * throws an exception
	 *
	 * @param code The code to evaluate
	 * @tparam T The type of value generated
	 * @return Either the value, or an error report if an exception was thrown
	 */
	def tryResult[T](code: => T): Result[T] = {
		Try(code) match {
			case Success(value) => Right(value)
			case Failure(e) => Left(e)
		}
	}

	/** Version of tryResult that allows a prefix for the error message
	 *
	 * @param code The code to evaluate
	 * @param prefix String prepended to the error message
	 * @tparam T The type of value generated
	 * @return Either the value, or an error report if an exception was thrown
	 */
	def tryResult[T](code: => T, prefix: => String): Result[T] = tryResultPrefix(prefix)(code)

	/** Version of tryResult that allows a prefix for the error message
	 *
	 * @param prefix String prepended to the error message
	 * @param code The code to evaluate
	 * @tparam T The type of value generated
	 * @return Either the value, or an error report if an exception was thrown
	 */
	def tryResultPrefix[T](prefix: => String)(code: => T): Result[T] = {
		Try(code) match {
			case Success(value) => Right(value)
			case Failure(e) => Left(ThrowableErrorReport(e, Some(prefix)))
		}
	}

	/** Results Result, or an Error if an exception is thrown
	 *
	 * @param code The code to evaluate
	 * @tparam T The type of Result generated
	 * @return Either the Result, or an error report if an exception was thrown
	 */
	def tryResultFlat[T](code: => Result[T]): Result[T] = {
		Try(code) match {
			case Success(value) => value
			case Failure(e) => Left(e)
		}
	}

	/** Converts a Try to a Result */
	def toResult[A](a: Try[A]): Result[A] = a match {
		case Success(value) => Right(value)
		case Failure(e) => Left(ThrowableErrorReport(e))
	}

	/** Returns error if condition is false */
	def toResult(condition: Boolean, error: => String): Result[Unit] = if (condition) Right(Unit) else Left(error)

	/** Call and return r, returning d on error */
	def tryElse[T](r: => T, d: => T): T = {
		try r catch {
			case NonFatal(_) => d
		}
	}

	/** Returns value in an option, or an error with given message if empty */
	def toResult[T](value: Option[T], noValueMessage: => String): Result[T] = value match {
		case Some(v) => Right(v)
		case None => Left(noValueMessage)
	}

	def addError[T](value: Result[T], errorPrefix: String): Result[T] = value match {
		case Right(_) => value
		case Left(e) => Left(StringErrorReport(s"$errorPrefix: ${e.description}"))
	}

	/** Maps a sequence with a function that can error, returning either the first error
	 * found, or the full mapped sequence
	 */
	def untilError[A, B, Repr, That](s: TraversableLike[A, Repr])(f: A => Result[B])(implicit bf: CanBuildFrom[Repr, B, That]): Result[That] = {
		def builder: mutable.Builder[B, That] = { // extracted to keep method size under 35 bytes, so that it can be JIT-inlined
			val b = bf(s.repr)
			b.sizeHint(s)
			b
		}
		val b = builder
		for (x <- s) {
			f(x) match {
				case Left(e) => return Left(e)
				case Right(v) => b += v
			}
		}
		Right(b.result)
	}

	implicit class FutureResult[T](val res: Future[Result[T]]) {
		def map[B](f: T => B)(implicit executor: ExecutionContext): FutureResult[B] = res.map(_.map(f))
		def flatMap[B](f: T => FutureResult[B])(implicit executor: ExecutionContext): FutureResult[B] = res flatMap {
			case Left(e) => Future.successful(Left(e))
			case Right(t) => f(t).res
		}
		def resultFlatMap[B](f: T => Result[B])(implicit executor: ExecutionContext): FutureResult[B] = res.map(_.flatMap(f))
		def futureFlatMap[B](f: T => Future[B])(implicit executor: ExecutionContext): FutureResult[B] = res flatMap {
			case Left(e) => Future.successful(Left(e))
			case Right(t) => f(t).map(Right(_))
		}
	}

	def sequence[T](seq: Seq[FutureResult[T]])(implicit executor: ExecutionContext): FutureResult[Seq[T]] = {
		Future.sequence(seq.map(_.res)).map(sequence(_))
	}

	def toFutureResult[T](f: Future[T])(implicit executor: ExecutionContext): FutureResult[T] = f.map(Right(_))

	def toFutureResult[T](r: Result[T])(implicit executor: ExecutionContext): FutureResult[T] = Future.successful(r)

	def combine[T](r: Try[Result[T]]): Result[T] = r match {
		case Failure(e) => Left(ThrowableErrorReport(e))
		case Success(t) => t
	}

	def toOption[T](condition: Boolean)(eval: => T): Option[T] = if (condition) Some(eval) else None
}
